'use client';

import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useRouter } from 'next/navigation';
import { CommandLoading } from 'cmdk';
import fuzzysort from 'fuzzysort';
import {
  Building2Icon,
  CheckCheckIcon,
  FlagIcon,
  RotateCcwIcon,
  RouteIcon,
  User2Icon,
  VenetianMaskIcon,
} from 'lucide-react';
import { toast } from 'sonner';

import { useAuthCtx } from '@/components/providers/auth-provider';
import { MODULE_DEFS, useModulesCtx } from '@/components/providers/modules-provider';
import { ApiSchemas, useApiV1Ctx } from '@/components/providers/v1-api';
import { useApiCustomers } from '@/components/providers/v1-cutsomer-provider';
import { CommandDialog, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { useDebouncedValue } from '@/hooks/use-debounced-value';
import { parseExternalId } from '@/lib/external_id';
import { formatAddress } from '@/lib/format';
import { cn } from '@/lib/utils';

type CmdTab = 'home' | 'office-switcher' | 'modules' | 'customers';

interface ContextType {
  query: string;
  tab: CmdTab;
  setTab: (v: CmdTab) => void;
  setShouldFilter: (v: boolean) => void;
  close: () => void;
}

const Context = createContext<ContextType | null>(null);

function useCmdCtx() {
  const ctx = useContext(Context);
  if (!ctx) {
    throw new Error('useCmdCtx must be used within a Provider');
  }
  return ctx;
}

export function CommandMenu() {
  const { isSuperuser, isActuallySuperuser } = useAuthCtx();
  const [inputValue, setInputValue] = useState('');
  const [open, setOpen] = useState(false);
  const [shouldFilter, setShouldFilter] = useState(true);
  const [tab, setTab] = useState<CmdTab>('home');
  const isHome = tab === 'home';

  useEffect(() => {
    if (!isSuperuser && !isActuallySuperuser) {
      return;
    }

    const down = (e: KeyboardEvent) => {
      if (e.key === 'k' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        setOpen((open) => !open);
      }
    };

    document.addEventListener('keydown', down);
    return () => document.removeEventListener('keydown', down);
  }, [isSuperuser, isActuallySuperuser]);

  useEffect(() => {
    if (!open) {
      setTab('home');
      setInputValue('');
    }
  }, [open]);

  const onChangeTab = useCallback((tab: CmdTab) => {
    setTab(tab);
    setInputValue('');
  }, []);

  const onClose = useCallback(() => {
    setOpen(false);
  }, []);

  return (
    <Context value={{ query: inputValue, tab, setTab: onChangeTab, setShouldFilter, close: onClose }}>
      <CommandDialog open={open} onOpenChange={setOpen} shouldFilter={shouldFilter}>
        <CommandInput
          placeholder='Type a command or search...'
          value={inputValue}
          onValueChange={(value) => {
            setInputValue(value);
          }}
          onKeyDown={(e) => {
            if (isHome || inputValue.length) {
              return;
            }
            if (e.key === 'Backspace') {
              setTab('home');
            }
          }}
        />
        <CommandList>
          {tab === 'home' && <CommandsHome />}
          {tab === 'office-switcher' && <CommandsOfficeSwitcher />}
          {tab === 'modules' && <CommandModules />}
          {tab === 'customers' && <CommandCustomers />}
        </CommandList>
      </CommandDialog>
    </Context>
  );
}

const NAV_ITEMS = [
  {
    label: 'Customers',
    icon: User2Icon,
    href: '/customers',
  },
  {
    label: 'Routes',
    icon: RouteIcon,
    href: '/routes',
  },
];

function CommandsHome() {
  const router = useRouter();
  const { setTab, close } = useCmdCtx();
  const { isSuperuser, isActuallySuperuser, toggleSuperuserView } = useAuthCtx();
  return (
    <>
      <CommandGroup heading='Admin actions'>
        {isSuperuser && (
          <CommandItem
            onSelect={() => {
              setTab('office-switcher');
            }}
          >
            <Building2Icon />
            <span>Switch Office</span>
          </CommandItem>
        )}
        {isSuperuser && (
          <CommandItem
            onSelect={() => {
              setTab('modules');
            }}
          >
            <FlagIcon />
            <span>Enabled Modules</span>
          </CommandItem>
        )}
        {isActuallySuperuser && (
          <CommandItem
            onSelect={() => {
              toggleSuperuserView();
            }}
          >
            <VenetianMaskIcon />
            <span>
              Switch to <span className='font-medium'>{isSuperuser ? 'regular' : 'superuser'}</span> view
            </span>
          </CommandItem>
        )}
      </CommandGroup>
      <CommandGroup heading='Search'>
        <CommandItem
          onSelect={() => {
            setTab('customers');
          }}
        >
          <User2Icon />
          <span>Search Customers</span>
        </CommandItem>
      </CommandGroup>
      <CommandGroup heading='Navigation'>
        {NAV_ITEMS.map((item) => (
          <CommandItem
            key={item.href}
            onSelect={() => {
              close();
              router.push(item.href);
            }}
          >
            <item.icon />
            <span>
              Go to <span className='font-medium'>{item.label}</span>
            </span>
          </CommandItem>
        ))}
      </CommandGroup>
    </>
  );
}

function CommandsOfficeSwitcher() {
  const { api } = useApiV1Ctx();
  const { close, query, setShouldFilter } = useCmdCtx();
  const { data: offices = [], isLoading } = api.offices.listAllOffices.useQuery();

  useEffect(() => {
    setShouldFilter(false);
    return () => setShouldFilter(true);
  }, [setShouldFilter]);

  const filtered = useMemo(() => {
    if (!query) return offices;
    const results = fuzzysort.go(query, offices, {
      key: 'name',
      threshold: 0.3,
      limit: 50,
    });
    return results.map((x) => x.obj);
  }, [offices, query]);

  const mutation = api.employees.switchToOffice.useMutation(undefined, {
    onSuccess: () => {
      toast.success('Switched to office employee');
      window.location.reload();
    },
  });

  return (
    <CommandGroup heading='Switch Office'>
      {isLoading && (
        <CommandLoading className='flex h-24 animate-pulse items-center justify-center rounded-lg bg-muted text-center text-sm font-medium text-muted-foreground'>
          Loading offices...
        </CommandLoading>
      )}
      {filtered.map((office) => (
        <CommandItem
          key={office.id}
          onSelect={() => {
            mutation.mutate({
              query: {
                office_id: office.id,
              },
            });
            close();
          }}
        >
          <Building2Icon />
          <span>{office.name}</span>
        </CommandItem>
      ))}
    </CommandGroup>
  );
}

function CommandModules() {
  const { isModuleEnabled, setModuleEnabled, enableAll, reset } = useModulesCtx();
  const modules = useMemo(() => {
    const modules = MODULE_DEFS.map((mod) => ({ name: mod.name, enabled: isModuleEnabled(mod.name) }));
    modules.sort((a, b) => a.name.localeCompare(b.name));
    return modules;
  }, [isModuleEnabled]);
  const enabledCount = useMemo(() => modules.filter((v) => v.enabled).length, [modules]);

  return (
    <>
      <CommandGroup heading='Quick actions'>
        <CommandItem key='enable-all-modules' onSelect={() => enableAll()}>
          <CheckCheckIcon />
          Enable all modules{' '}
          <span className='text-muted-foreground'>
            ({enabledCount} / {MODULE_DEFS.length})
          </span>
        </CommandItem>
        <CommandItem key='reset-modules' onSelect={() => reset()}>
          <RotateCcwIcon />
          Reset module overwrites
        </CommandItem>
      </CommandGroup>
      <CommandGroup heading='All modules'>
        {modules.map((v) => (
          <CommandItem key={v.name} onSelect={() => setModuleEnabled(v.name, !v.enabled)}>
            <div className={cn('mr-1 size-2 rounded-full border', v.enabled && 'border-primary bg-primary')} />
            {v.name}
          </CommandItem>
        ))}
      </CommandGroup>
    </>
  );
}

function CommandCustomers() {
  const { query, setShouldFilter } = useCmdCtx();
  const debounced = useDebouncedValue(query, 200);
  const { data, isLoading, isFetching } = useApiCustomers({
    query: debounced,
    limit: 20,
    page: 1,
  });

  useEffect(() => {
    setShouldFilter(false);
    return () => {
      setShouldFilter(true);
    };
  }, [setShouldFilter]);

  return (
    <CommandGroup heading='Customers' className={isFetching ? 'animate-pulse' : undefined}>
      {isLoading && (
        <CommandLoading className='flex h-24 animate-pulse items-center justify-center rounded-lg bg-muted text-center text-sm font-medium text-muted-foreground'>
          Loading customers...
        </CommandLoading>
      )}
      {data?.items.map((customer) => (
        <CustomerItem key={customer.id} customer={customer} />
      ))}
    </CommandGroup>
  );
}

function CustomerItem({ customer }: { customer: ApiSchemas['CustomerRead'] }) {
  const router = useRouter();
  const { close } = useCmdCtx();
  const { mainName, secondaryName, externalId } = useMemo(() => {
    const companyName = customer.company ?? '';
    const fullName = `${customer.first_name} ${customer.last_name}`.trim();
    const externalId = parseExternalId(customer.external_id, 'customer');
    if (companyName) {
      return { mainName: companyName, secondaryName: fullName, externalId };
    }
    return { mainName: fullName, secondaryName: null, externalId };
  }, [customer.company, customer.first_name, customer.last_name, customer.external_id]);
  const address = useMemo(() => {
    const primaryLocation = customer.locations.find((location) => location.primary) ?? customer.locations[0];
    return primaryLocation ? formatAddress(primaryLocation) : null;
  }, [customer.locations]);

  return (
    <CommandItem
      value={customer.id}
      onSelect={() => {
        router.push(`/customers/${customer.id}`);
        close();
      }}
      className='gap-3'
    >
      <User2Icon />
      <div className='flex flex-col'>
        <div className='inline-flex items-center gap-1.5'>
          <span>{mainName}</span>
          {externalId && <span className='text-muted-foreground'>#{externalId}</span>}
        </div>
        {secondaryName && <span className=''>{secondaryName}</span>}
        {address && <span className='text-muted-foreground'>{address}</span>}
      </div>
    </CommandItem>
  );
}
